import { Select } from "antd";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import useSetActiveBranch from "../hooks/useSetActiveBranch";
import useApi from "../utils/useApi";

const { Option, OptGroup } = Select;

const BranchSelector = () => {
  const { user } = useAuth();
  const { changeActiveBranch, isLoading } = useSetActiveBranch();
  const { getBranches, getClients } = useApi();

  const [allBranches, setAllBranches] = useState([]);
  const [clients, setClients] = useState([]);

  // Load all branches and clients for superusers
  useEffect(() => {
    const loadData = async () => {
      if (user?.is_superuser) {
        try {
          const [branchesRes, clientsRes] = await Promise.all([
            getBranches(),
            getClients(),
          ]);
          setAllBranches(branchesRes?.data || branchesRes || []);
          setClients(clientsRes?.data || clientsRes || []);
        } catch (error) {
          console.error("Error loading branches and clients:", error);
        }
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.is_superuser]);

  const handleBranchChange = (branchId) => {
    changeActiveBranch(branchId);
  };

  const clientMap = clients.reduce((map, client) => {
    map[client.id] = client.name;
    return map;
  }, {});

  const branchesToShow = user?.is_superuser
    ? allBranches
    : user?.available_branches || [];

  const normalizedBranches = branchesToShow.map((branch) => {
    const clientName = user?.is_superuser
      ? clientMap[branch.client] || "Sem empresa"
      : branch.client_name || "Sem empresa";

    const clientId = user?.is_superuser ? branch.client : branch.client_id;

    return {
      id: branch.id,
      name: branch.name,
      clientId,
      clientName,
      label: (
        <span>
          <span style={{ fontWeight: 600, color: "rgba(0, 0, 0, 0.88)" }}>
            {clientName}
          </span>
          <span style={{ color: "rgba(0, 0, 0, 0.72)" }}> - {branch.name}</span>
        </span>
      ),
    };
  });

  const branchesByClient = normalizedBranches.reduce((acc, branch) => {
    const key = `${branch.clientId ?? "no-client"}::${branch.clientName}`;
    if (!acc[key]) {
      acc[key] = {
        clientName: branch.clientName,
        branches: [],
      };
    }
    acc[key].branches.push(branch);
    return acc;
  }, {});

  // Hide the select if user is only linked to one Branch
  if (!branchesToShow || branchesToShow.length < 2) {
    return null;
  }

  const sortedGroups = Object.entries(branchesByClient).sort(([, a], [, b]) =>
    a.clientName.localeCompare(b.clientName, "pt-BR")
  );

  return (
    <div
      className="ouran-branch-selector-wrap"
      style={{ display: "flex", alignItems: "center", marginRight: 10 }}>
      <Select
        value={user.active_branch_id}
        onChange={handleBranchChange}
        loading={isLoading}
        popupClassName="branch-selector-dropdown"
        style={{
          width: 250,
        }}
        placeholder="Selecione a filial"
        size="middle"
        optionLabelProp="label">
        {sortedGroups.map(([groupKey, group]) => (
          <OptGroup
            key={groupKey}
            label={
              <span
                style={{
                  fontWeight: 600,
                  fontSize: "12px",
                  color: "rgba(0, 0, 0, 0.88)",
                }}>
                {group.clientName}
              </span>
            }>
            {[...group.branches]
              .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"))
              .map((branch) => (
                <Option key={branch.id} value={branch.id} label={branch.label}>
                  <span
                    style={{
                      display: "inline-block",
                      paddingLeft: 14,
                      color: "rgba(0, 0, 0, 0.72)",
                    }}>
                    {branch.name}
                  </span>
                </Option>
              ))}
          </OptGroup>
        ))}
      </Select>
    </div>
  );
};

export default BranchSelector;
